import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';

// Leaflet map for Area Search Coordinator. Sibling to CanvassMap.js (not a
// modification of it) — same vanilla-Leaflet, ref-based init-once idioms.
// Renders a read-only search-boundary overlay, one colored polyline per
// participant's live trail, dropped pins, and a local "you are here" marker;
// also hosts the hand-rolled boundary-drawing interaction used by the create
// screen (no leaflet-draw dependency — click-driven, so it degrades cleanly
// to touch devices with no hover).

const DEFAULT_CENTER = { lat: 41.03, lng: -73.76, zoom: 13 };
const CLOSE_VERTEX_PX = 14; // click-near-first-vertex tolerance for closing a polygon
const DRAFT_STYLE = { color: '#0891b2', weight: 2, dashArray: '6 4', fillOpacity: 0.08, interactive: false };
const BOUNDARY_STYLE = { color: '#0891b2', weight: 2, dashArray: '6 4', fillOpacity: 0.06, interactive: false };

// Grid search mode: coverage tiers for a cell. `none` = not yet walked,
// `possible` = one auto-detected trail hit (could be GPS noise, so amber, not
// green), `confirmed` = 2+ hits or a manual mark.
const TIER_STYLE = {
  none:      { color: '#94a3b8', weight: 1,   fillColor: '#94a3b8', fillOpacity: 0.05 },
  possible:  { color: '#d97706', weight: 1.5, fillColor: '#f59e0b', fillOpacity: 0.35 },
  confirmed: { color: '#16a34a', weight: 1.5, fillColor: '#16a34a', fillOpacity: 0.45 },
};

function gridLabelIcon(code) {
  return L.divIcon({
    html: `<div style="position:absolute;left:0;top:0;transform:translate(-50%,-50%);font-size:10px;font-weight:700;color:#334155;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;white-space:nowrap;pointer-events:none;">${code}</div>`,
    className: 'areasearch-grid-label',
    iconSize: [0, 0],
  });
}

function pinIcon(color) {
  return L.divIcon({
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1.5px ${color};"></div>`,
    className: 'areasearch-pin',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

const hereIcon = L.divIcon({
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#0f172a;border:3px solid #fff;box-shadow:0 0 0 2px #0f172a, 0 0 0 8px rgba(15,23,42,.18);"></div>`,
  className: 'areasearch-here',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export default function AreaSearchMap({
  boundary = null,          // { type:'circle', center:{lat,lng}, radiusMeters } | { type:'polygon', points:[{lat,lng}] }
  trails = [],               // [{ participantId, color, path:[{lat,lng}] }]
  pins = [],                  // [{ id, lat, lng, color, label }]
  gridCells = [],              // [{ id, code, boundsSouth/West/North/East, tier }] — grid search mode
  youAreHere = null,          // { lat, lng }
  center,
  addPinMode = false,
  onMapClick,
  onPinClick,
  onPinDragEnd,
  onGridCellClick,             // (cellId) => void — grid search mode manual toggle
  drawMode = null,             // null | 'circle' | 'polygon' — create-screen boundary drawing
  onBoundaryDraftChange,       // (draft) => void, fired on every draw interaction
  resetToken = 0,               // bump to clear the in-progress draft (e.g. Cancel)
  height = 460,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const boundaryLayerRef = useRef(null);
  const gridLayerRef = useRef(null);
  const trailsLayerRef = useRef(null);
  const pinsLayerRef = useRef(null);
  const draftLayerRef = useRef(null);
  const hereMarkerRef = useRef(null);
  const didFitRef = useRef(false);
  const draftRef = useRef(null);
  const lastMoveRef = useRef(0);

  const propsRef = useRef({});
  propsRef.current = { addPinMode, onMapClick, onPinClick, onPinDragEnd, onGridCellClick, drawMode, onBoundaryDraftChange };

  function emitDraft() {
    if (propsRef.current.onBoundaryDraftChange) propsRef.current.onBoundaryDraftChange(draftRef.current);
  }

  function redrawDraft(overrideDraft) {
    const layer = draftLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const d = overrideDraft || draftRef.current;
    if (!d) return;
    if (d.type === 'circle') {
      L.circle([d.center.lat, d.center.lng], { radius: d.radiusMeters || 1, ...DRAFT_STYLE }).addTo(layer);
      L.circleMarker([d.center.lat, d.center.lng], { radius: 4, color: '#0891b2', fillOpacity: 1 }).addTo(layer);
    } else if (d.type === 'polygon') {
      const pts = d.points.map(p => [p.lat, p.lng]);
      const previewPts = d.__previewPoint ? [...pts, [d.__previewPoint.lat, d.__previewPoint.lng]] : pts;
      if (d.closed) {
        L.polygon(pts, DRAFT_STYLE).addTo(layer);
      } else if (previewPts.length > 1) {
        L.polyline(previewPts, DRAFT_STYLE).addTo(layer);
      }
      pts.forEach(p => L.circleMarker(p, { radius: 4, color: '#0891b2', fillOpacity: 1, interactive: false }).addTo(layer));
    }
  }

  function resetDraft() {
    draftRef.current = null;
    if (draftLayerRef.current) draftLayerRef.current.clearLayers();
    emitDraft();
  }

  function handleDrawClick(latlng) {
    const mode = propsRef.current.drawMode;
    const map = mapRef.current;
    if (!map || !mode) return;

    if (mode === 'circle') {
      const d = draftRef.current;
      if (!d || d.closed) {
        draftRef.current = { type: 'circle', center: { lat: latlng.lat, lng: latlng.lng }, radiusMeters: 0, closed: false };
      } else {
        const radiusMeters = map.distance(L.latLng(d.center.lat, d.center.lng), latlng);
        draftRef.current = { ...d, radiusMeters, closed: true };
      }
      emitDraft();
      redrawDraft();
      return;
    }

    if (mode === 'polygon') {
      const d = draftRef.current;
      if (!d || d.closed) {
        draftRef.current = { type: 'polygon', points: [{ lat: latlng.lat, lng: latlng.lng }], closed: false };
        emitDraft();
        redrawDraft();
        return;
      }
      if (d.points.length >= 3) {
        const first = d.points[0];
        const p1 = map.latLngToContainerPoint(latlng);
        const p0 = map.latLngToContainerPoint([first.lat, first.lng]);
        if (p1.distanceTo(p0) <= CLOSE_VERTEX_PX) {
          draftRef.current = { ...d, closed: true };
          emitDraft();
          redrawDraft();
          return;
        }
      }
      draftRef.current = { ...d, points: [...d.points, { lat: latlng.lat, lng: latlng.lng }] };
      emitDraft();
      redrawDraft();
    }
  }

  function handleDrawPreview(latlng) {
    const d = draftRef.current;
    if (!d || d.closed) return;
    const map = mapRef.current;
    if (d.type === 'circle') {
      const radiusMeters = map.distance(L.latLng(d.center.lat, d.center.lng), latlng);
      redrawDraft({ ...d, radiusMeters, __preview: true });
    } else if (d.type === 'polygon') {
      redrawDraft({ ...d, __previewPoint: { lat: latlng.lat, lng: latlng.lng } });
    }
  }

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const c = center || DEFAULT_CENTER;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true, preferCanvas: true });
    map.setView([c.lat, c.lng], c.zoom || DEFAULT_CENTER.zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    boundaryLayerRef.current = L.layerGroup().addTo(map);
    gridLayerRef.current = L.layerGroup().addTo(map);
    trailsLayerRef.current = L.layerGroup().addTo(map);
    pinsLayerRef.current = L.layerGroup().addTo(map);
    draftLayerRef.current = L.layerGroup().addTo(map);

    map.on('click', e => {
      if (propsRef.current.drawMode) {
        handleDrawClick(e.latlng);
      } else if (propsRef.current.addPinMode && propsRef.current.onMapClick) {
        propsRef.current.onMapClick(e.latlng.lat, e.latlng.lng);
      }
    });
    map.on('mousemove', e => {
      if (!propsRef.current.drawMode) return;
      const now = Date.now();
      if (now - lastMoveRef.current < 40) return; // light throttle on preview redraw
      lastMoveRef.current = now;
      handleDrawPreview(e.latlng);
    });

    mapRef.current = map;
    const t = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(false); }, 60);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      boundaryLayerRef.current = null;
      gridLayerRef.current = null;
      trailsLayerRef.current = null;
      pinsLayerRef.current = null;
      draftLayerRef.current = null;
      hereMarkerRef.current = null;
      didFitRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Crosshair cursor while adding a pin or drawing a boundary.
  useEffect(() => {
    if (containerRef.current) containerRef.current.style.cursor = (addPinMode || drawMode) ? 'crosshair' : '';
  }, [addPinMode, drawMode]);

  // Clear the in-progress draft whenever the drawing mode is (re)selected or
  // the caller explicitly asks for a reset (e.g. the Cancel button).
  useEffect(() => {
    resetDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMode, resetToken]);

  // Re-center when the caller changes it (e.g. after geolocating on create).
  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setView([center.lat, center.lng], center.zoom || mapRef.current.getZoom());
  }, [center]);

  // Fit to the confirmed boundary once, if the caller gave no explicit center.
  useEffect(() => {
    if (didFitRef.current || center || !mapRef.current || !boundary) return;
    let bounds = null;
    if (boundary.type === 'circle' && boundary.center && boundary.radiusMeters) {
      // A circle's real getBounds() needs the layer attached to a map (it
      // projects through this._map); this runs before that, so approximate
      // the bounding box in degrees directly — plenty accurate for framing.
      const latDelta = boundary.radiusMeters / 111320;
      const lngDelta = boundary.radiusMeters / (111320 * Math.cos((boundary.center.lat * Math.PI) / 180) || 1);
      bounds = L.latLngBounds(
        [boundary.center.lat - latDelta, boundary.center.lng - lngDelta],
        [boundary.center.lat + latDelta, boundary.center.lng + lngDelta],
      );
    } else if (boundary.type === 'polygon' && Array.isArray(boundary.points) && boundary.points.length >= 3) {
      bounds = L.latLngBounds(boundary.points.map(p => [p.lat, p.lng]));
    }
    if (bounds && bounds.isValid()) {
      mapRef.current.fitBounds(bounds.pad(0.15), { maxZoom: 17 });
      didFitRef.current = true;
    }
  }, [boundary, center]);

  // Read-only boundary overlay.
  useEffect(() => {
    const layer = boundaryLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!boundary) return;
    if (boundary.type === 'circle' && boundary.center && boundary.radiusMeters) {
      L.circle([boundary.center.lat, boundary.center.lng], { radius: boundary.radiusMeters, ...BOUNDARY_STYLE }).addTo(layer);
    } else if (boundary.type === 'polygon' && Array.isArray(boundary.points) && boundary.points.length >= 3) {
      L.polygon(boundary.points.map(p => [p.lat, p.lng]), BOUNDARY_STYLE).addTo(layer);
    }
  }, [boundary]);

  // Grid search mode: colored cell rectangles + short code labels. Clear +
  // rebuild on every change, same simplicity-over-diffing idiom used
  // throughout this component.
  useEffect(() => {
    const layer = gridLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const cell of gridCells) {
      const style = TIER_STYLE[cell.tier] || TIER_STYLE.none;
      const rect = L.rectangle(
        [[cell.boundsSouth, cell.boundsWest], [cell.boundsNorth, cell.boundsEast]],
        style,
      );
      rect.on('click', e => {
        L.DomEvent.stopPropagation(e);
        if (propsRef.current.drawMode || propsRef.current.addPinMode) return;
        propsRef.current.onGridCellClick && propsRef.current.onGridCellClick(cell.id);
      });
      rect.bindTooltip(`${cell.code}${cell.tier !== 'none' ? ` · ${cell.tier}` : ''}`, { sticky: true });
      rect.addTo(layer);
      L.marker([cell.centerLat ?? (cell.boundsSouth + cell.boundsNorth) / 2, cell.centerLng ?? (cell.boundsWest + cell.boundsEast) / 2], {
        icon: gridLabelIcon(cell.code), interactive: false,
      }).addTo(layer);
    }
  }, [gridCells]);

  // Per-participant trail polylines — clear + rebuild on every change, same
  // simplicity-over-diffing idiom CanvassMap uses for its markers.
  useEffect(() => {
    const layer = trailsLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const t of trails) {
      if (!t.path || t.path.length === 0) continue;
      const color = t.color || '#334155';
      if (t.path.length === 1) {
        L.circleMarker([t.path[0].lat, t.path[0].lng], { radius: 4, color, fillOpacity: 1 }).addTo(layer);
        continue;
      }
      L.polyline(t.path.map(p => [p.lat, p.lng]), { color, weight: 4, opacity: 0.85, lineCap: 'round', lineJoin: 'round' }).addTo(layer);
    }
  }, [trails]);

  // Dropped pins.
  useEffect(() => {
    const layer = pinsLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const p of pins) {
      if (p.lat == null || p.lng == null) continue;
      const m = L.marker([p.lat, p.lng], { icon: pinIcon(p.color || '#0f172a'), draggable: true });
      if (p.label) m.bindTooltip(p.label, { direction: 'top' });
      m.on('click', () => propsRef.current.onPinClick && propsRef.current.onPinClick(p.id));
      m.on('dragend', ev => {
        const ll = ev.target.getLatLng();
        propsRef.current.onPinDragEnd && propsRef.current.onPinDragEnd(p.id, ll.lat, ll.lng);
      });
      m.addTo(layer);
    }
  }, [pins]);

  // Local "you are here" marker — driven straight from the tracking hook's
  // live state, not read back from Firestore, for zero-latency feedback.
  useEffect(() => {
    if (!mapRef.current) return;
    if (hereMarkerRef.current) { hereMarkerRef.current.remove(); hereMarkerRef.current = null; }
    if (youAreHere && youAreHere.lat != null && youAreHere.lng != null) {
      hereMarkerRef.current = L.marker([youAreHere.lat, youAreHere.lng], {
        icon: hereIcon, zIndexOffset: 900, interactive: false,
      }).addTo(mapRef.current);
    }
  }, [youAreHere]);

  return <Box ref={containerRef} sx={{ height, width: '100%', borderRadius: '10px', overflow: 'hidden', zIndex: 0 }} />;
}
